import { createAsyncThunk } from '@reduxjs/toolkit';
import { 
    setLoading, 
    setSubscriptionData, 
    setError, 
    clearError,
    SubscriptionData 
} from '../slices/subscriptionSlice';
import { 
    getCustomerSubscriptions, 
    getAvailablePlans, 
    createPaymentLink,
    getCheckoutSession,
    createCustomerSession
} from '../../common/backend-api/backend-api';
import { RootState } from '../store';

// Thunk to fetch customer subscriptions
export const fetchCustomerSubscriptionsThunk = createAsyncThunk<
    SubscriptionData | null,
    void,
    { rejectValue: string, state: RootState }
>(
    'subscription/fetchCustomerSubscriptions',
    async (_, { dispatch, rejectWithValue, getState }) => {
        dispatch(setLoading(true));
        dispatch(clearError());
        
        try {
            // Fetch customer subscriptions
            const result = await dispatch(getCustomerSubscriptions.initiate()).unwrap();
            
            if (result.success) {
                let subscriptionData = result.customerData;
                
                // Check if user has active subscriptions
                const hasActiveSubscription = subscriptionData?.subscriptions?.some(
                    (sub: any) => sub.status === 'active' || sub.status === 'trialing'
                );
                
                // If no active subscription, create customer session for pricing table
                if (!hasActiveSubscription) {
                    try {
                        console.log('No active subscription found, creating customer session for pricing table');
                        const customerSessionResult = await dispatch(createCustomerSession.initiate()).unwrap();
                        
                        if (customerSessionResult.success) {
                            // Create a new object with the customer session secret (RTK result is immutable)
                            subscriptionData = {
                                ...subscriptionData,
                                customerSessionSecret: customerSessionResult.clientSecret
                            };
                            console.log('Customer session created successfully');
                        }
                    } catch (sessionError) {
                        console.warn('Failed to create customer session:', sessionError);
                        // Continue without customer session - will fall back to email pre-fill
                    }
                }
                
                dispatch(setSubscriptionData(subscriptionData));
                return subscriptionData;
            } else {
                // No subscription data, try to create customer session for new user
                try {
                    console.log('No customer data found, creating customer session for new user');
                    const customerSessionResult = await dispatch(createCustomerSession.initiate()).unwrap();
                    
                    if (customerSessionResult.success) {
                        const newSubscriptionData = {
                            customerId: customerSessionResult.customerId,
                            subscriptions: [],
                            customerSessionSecret: customerSessionResult.clientSecret
                        };
                        dispatch(setSubscriptionData(newSubscriptionData));
                        return newSubscriptionData;
                    }
                } catch (sessionError) {
                    console.warn('Failed to create customer session for new user:', sessionError);
                }
                
                // Fallback: no subscription data
                dispatch(setSubscriptionData(null));
                return null;
            }
        } catch (error: any) {
            console.error('Error fetching customer subscriptions:', error);
            const errorMessage = error.message || 'Failed to load subscription data';
            dispatch(setError(errorMessage));
            dispatch(setSubscriptionData(null));
            return rejectWithValue(errorMessage);
        } finally {
            dispatch(setLoading(false));
        }
    }
);

// Thunk to fetch available plans
export const fetchAvailablePlansThunk = createAsyncThunk<
    any[],
    void,
    { rejectValue: string }
>(
    'subscription/fetchAvailablePlans',
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const result = await dispatch(getAvailablePlans.initiate()).unwrap();
            
            if (result.success && result.plans) {
                return result.plans;
            } else {
                throw new Error('Failed to load subscription plans');
            }
        } catch (error: any) {
            console.error('Error fetching available plans:', error);
            const errorMessage = error.message || 'Failed to load subscription plans';
            return rejectWithValue(errorMessage);
        }
    }
);

// Thunk to create payment link and redirect
export const createPaymentLinkThunk = createAsyncThunk<
    void,
    { priceId: string; successUrl?: string; cancelUrl?: string },
    { rejectValue: string }
>(
    'subscription/createPaymentLink',
    async ({ priceId, successUrl, cancelUrl }, { dispatch, rejectWithValue }) => {
        try {
            // Get current URL for redirect if not provided
            const currentUrl = window.location.origin;
            const defaultSuccessUrl = `${currentUrl}/account-settings?subscription=success`;
            const defaultCancelUrl = `${currentUrl}/account-settings?subscription=cancelled`;
            
            const result = await dispatch(createPaymentLink.initiate({
                priceId,
                successUrl: successUrl || defaultSuccessUrl,
                cancelUrl: cancelUrl || defaultCancelUrl
            })).unwrap();
            
            if (result.success && result.paymentLink) {
                // Redirect to Stripe payment page
                window.location.href = result.paymentLink.url;
            } else {
                throw new Error('Failed to create payment link');
            }
        } catch (error: any) {
            console.error('Error creating payment link:', error);
            const errorMessage = error.message || 'Failed to create payment link';
            return rejectWithValue(errorMessage);
        }
    }
);

// Thunk to refresh subscription data (force refetch)
export const refreshSubscriptionDataThunk = createAsyncThunk<
    SubscriptionData | null,
    void,
    { rejectValue: string, state: RootState }
>(
    'subscription/refreshSubscriptionData',
    async (_, { dispatch, rejectWithValue }) => {
        // Force refresh by calling the thunk again
        const result = await dispatch(fetchCustomerSubscriptionsThunk());
        if (fetchCustomerSubscriptionsThunk.fulfilled.match(result)) {
            return result.payload;
        } else {
            return rejectWithValue(result.payload || 'Failed to refresh subscription data');
        }
    }
);

// Thunk to fetch checkout session information
export const fetchCheckoutSessionThunk = createAsyncThunk<
    any,
    string,
    { rejectValue: string }
>(
    'subscription/fetchCheckoutSession',
    async (sessionId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(clearError());
            
            const result = await dispatch(getCheckoutSession.initiate({ sessionId })).unwrap();
            
            if (result.success) {
                return result.sessionInfo;
            } else {
                return rejectWithValue('Failed to fetch checkout session');
            }
        } catch (error: any) {
            console.error('Error fetching checkout session:', error);
            const errorMessage = error.message || 'Failed to fetch checkout session information';
            dispatch(setError(errorMessage));
            return rejectWithValue(errorMessage);
        }
    }
);

// Selector helper to check if data is stale (older than 5 minutes)
export const isSubscriptionDataStale = (state: RootState): boolean => {
    const { lastFetched } = state.subscription;
    if (!lastFetched) return true;
    
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    return lastFetched < fiveMinutesAgo;
};

// Thunk to fetch subscription data only if needed (cached or stale)
export const fetchSubscriptionDataIfNeededThunk = createAsyncThunk<
    SubscriptionData | null,
    void,
    { rejectValue: string, state: RootState }
>(
    'subscription/fetchSubscriptionDataIfNeeded',
    async (_, { dispatch, getState }) => {
        const state = getState();
        
        // Skip if currently loading
        if (state.subscription.loading) {
            return state.subscription.data;
        }
        
        // Fetch if no data or data is stale
        if (!state.subscription.data || isSubscriptionDataStale(state)) {
            return dispatch(fetchCustomerSubscriptionsThunk()).unwrap();
        }
        
        return state.subscription.data;
    }
); 